import { useEffect, useState, useRef, RefObject, EffectCallback } from 'react';

/**
 * A hook that observes a container's width and determines if it meets a breakpoint
 * @param breakpoint The width in pixels at which the layout should change
 * @returns An object containing the container ref and whether the container is wide enough
 */
export function useContainerQuery<T extends HTMLElement = HTMLDivElement>(breakpoint: number) {
  const containerRef = useRef<T>(null);
  const [isWideLayout, setIsWideLayout] = useState(false);

  useEffect((): ReturnType<EffectCallback> => {
    const container = containerRef.current;
    if (!container) return undefined;

    // Initial check
    setIsWideLayout(container.offsetWidth >= breakpoint);

    // Set up ResizeObserver to watch for container size changes
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setIsWideLayout(width >= breakpoint);
    });

    observer.observe(container);

    // Clean up observer on unmount
    return () => {
      observer.disconnect();
    };
  }, [breakpoint]);

  return { containerRef, isWideLayout };
}

export default useContainerQuery;
